import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Description, Download, Upload } from "@mui/icons-material";
import {
    PHOTOGRAMMETRY_MODULE_UUID,
    PROCESS_STATUS,
    PROCESS_STATUS_STYLE,
} from "../../../constants";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { JobNodeProcessInfo } from "./JobNodeProcessInfo";
import { LogView } from "./LogView";
import { MetashapeUploadDialog } from "./MetashapeUploadDialog";
import { ModuleDownloadContent } from "./ModuleDownloadContent";
import { camelCaseToTitleCase } from "../../../utils";
import useSWR from "swr";
import { useState } from "react";

export function JobNodeHeader({ job, moduleVersion }) {
    const processUuid = job.processUuid;

    const {
        data: process,
        error: processFetchError,
        isLoading: processIsLoading,
    } = useSWR(`${BACKEND_ROUTES.PROCESS}/${processUuid}`);

    if (processFetchError) return <FetchErrorAlert error={processFetchError} />;
    if (processIsLoading)
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="3em"
            />
        );

    const imageName = moduleVersion.imageName.split("/").pop();

    return (
        <Stack direction="column" sx={{ px: 1.5, pt: 1.5 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
            >
                <ProcessStatusIcon processStatus={process.status} />
                <Typography variant="body1" fontWeight="bold">
                    {camelCaseToTitleCase(job.name)}
                </Typography>
                <Tooltip title={imageName} arrow placement="top">
                    <Chip
                        label={moduleVersion.version}
                        size="small"
                        sx={{
                            fontFamily: "Consolas, monospace",
                        }}
                    />
                </Tooltip>
            </Stack>
            <ProcessActions
                jobName={job.name}
                process={process}
                moduleVersion={moduleVersion}
            />
            <JobNodeProcessInfo process={process} />
        </Stack>
    );
}

function ProcessStatusIcon({ processStatus }) {
    const { color, icon: StatusIcon } = PROCESS_STATUS_STYLE[processStatus];
    return (
        <Tooltip title={processStatus} arrow placement="top">
            <StatusIcon sx={{ color }} />
        </Tooltip>
    );
}

const PROCESS_ACTIONS = Object.freeze({
    VIEW_LOGS: "ViewLogs",
    DOWNLOAD_RESULTS: "DownloadResults",
    UPLOAD_LOCAL_METASHAPE: "UploadLocalMetashape",
});
function ProcessActions({ jobName, process, moduleVersion }) {
    const [open, setOpen] = useState(false);
    const [action, setAction] = useState(null);

    const isPhotogrammetry =
        moduleVersion.moduleUuid === PHOTOGRAMMETRY_MODULE_UUID;

    const viewLogsDisabled = !process.log;
    const downloadResultsDisabled = [
        PROCESS_STATUS.UNPLANNED,
        PROCESS_STATUS.PLANNED,
        PROCESS_STATUS.EXECUTING,
    ].includes(process.status);
    const uploadMetashapeDisabled = [PROCESS_STATUS.EXECUTING].includes(
        process.status
    );

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="center">
                <Tooltip title="View logs" arrow placement="bottom">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setOpen(true);
                                setAction(PROCESS_ACTIONS.VIEW_LOGS);
                            }}
                            disabled={viewLogsDisabled}
                        >
                            <Description fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Download results" arrow placement="bottom">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setOpen(true);
                                setAction(PROCESS_ACTIONS.DOWNLOAD_RESULTS);
                            }}
                            disabled={downloadResultsDisabled}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {isPhotogrammetry && (
                    <>
                        <Divider
                            orientation="vertical"
                            variant="middle"
                            flexItem
                            sx={{
                                borderColor: (theme) => theme.palette.grey[600],
                                mx: 1,
                            }}
                        />
                        <Tooltip
                            title="Upload local metashape project"
                            arrow
                            placement="bottom"
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setOpen(true);
                                        setAction(
                                            PROCESS_ACTIONS.UPLOAD_LOCAL_METASHAPE
                                        );
                                    }}
                                    disabled={uploadMetashapeDisabled}
                                >
                                    <Upload fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}
            </Stack>
            <Dialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    setAction(null);
                }}
                maxWidth="md"
                fullWidth
            >
                {action === PROCESS_ACTIONS.VIEW_LOGS ? (
                    <LogView jobName={jobName} process={process} />
                ) : action === PROCESS_ACTIONS.DOWNLOAD_RESULTS ? (
                    <>
                        <DialogTitle>
                            {camelCaseToTitleCase(jobName)}
                        </DialogTitle>
                        <DialogContent>
                            <ModuleDownloadContent
                                process={process}
                                moduleVersion={moduleVersion}
                            />
                        </DialogContent>
                    </>
                ) : action === PROCESS_ACTIONS.UPLOAD_LOCAL_METASHAPE &&
                  isPhotogrammetry ? (
                    <MetashapeUploadDialog photogrammetryProcess={process} />
                ) : null}
            </Dialog>
        </>
    );
}
